import MENU_ROLES from '@/constants/ConstantMenuRoles';
import TIPO_NAVEGACAO_PERFIL from '@/constants/ConstantTipoNavegacaoPerfil';
import USER_ROLES from '@/constants/ConstantUserRole';
import ServiceAdmin from '@/services/ServiceAdmin';
import ServiceAuth from '@/services/ServiceAuth';

import UtilsMask from '@/utils/UtilsMask';
import { cloneDeep, groupBy, sortBy, uniqueId } from 'lodash';
import { defineStore } from 'pinia';

export const useStoreAuth = defineStore('auth', {
  state: () => ({
    validPassword: false,
    showModal: false,
    token: localStorage.getItem('access_Token'),
    tokenExpirationDate: localStorage.getItem('access_token_expiration_date'),
    refreshToken: '',
    error: '',
    credentials: {},
    authorities: [],
    userValidated: false,
    requireToken: false,
    validationUserErrors: '',
    moduleMessage: '',
    thumbnail: null,
    user: {},
    pendency: false,
    adminMenus: [],
    userEstablishments: [],
    tipoNavegacaoPerfil: null,
    loadingUserEstablishments: true,
  }),

  getters: {
    hasMenuSettings(state) {
      return state.authorities.includes('ROLE_ADMIN');
    },
    hasMenuHelp(state) {
      return state.authorities.includes('ROLE_ADMIN');
    },
    getUserMail(state) {
      return state.user?.emailAddress;
    },
    getTfaEnabled(state) {
      return state.user?.twoFactorAuthenticationEnabled;
    },
    isAdmin(state) {
      return state.authorities.includes('ROLE_ADMIN');
    },
    userRootCnpjs(state) {
      return state.userEstablishments.filter((item) => !item.details?.matriz);
    },
    parsedRootCnpjs(state) {
      return parseListWithoutGroups(state.userRootCnpjs);
    },
    parsedEstablishments(state) {
      const establishmentsGroupedBySocialName = getEstablishmentsGroupedBySocialName(state.userEstablishments);
      const listGroupItems = parseListGroupItems(establishmentsGroupedBySocialName);

      return separateAndFlattenGroupItems(listGroupItems);
    },
    isPerfilAnalista(state) {
      return state.tipoNavegacaoPerfil === TIPO_NAVEGACAO_PERFIL.ANALISTA;
    },
  },

  actions: {
    async getUser() {
      try {
        const { data } = await ServiceAuth.user.getUser();

        if (data) {
          const user = {
            id: data.id,
            firstName: data.firstName,
            lastName: data.lastName,
            username: data.username,
            cellPhone: data.cellPhone,
            emailAddress: data.emailAddress,
            authorities: data.authorities,
            profiles: data.profiles ?? [],
            thumbnailId: data.thumbnailId,
            twoFactorAuthenticationEnabled: data.twoFactorAuthenticationEnabled,
          };

          this.authorities = user.authorities;
          this.user = user;

          this.tipoNavegacaoPerfil = this.authorities.some((i) => MENU_ROLES.analistaAgnes.includes(i))
            ? TIPO_NAVEGACAO_PERFIL.ANALISTA
            : TIPO_NAVEGACAO_PERFIL.USUARIO;

          return user;
        }
      } catch (e) {
        this.errorSubmit(e);
      }
    },

    setMessage(message) {
      this.moduleMessage = message;
    },

    async saveUser(payload) {
      payload.thumbnailId = this.user.thumbnailId;
      payload.twoFactorAuthenticationEnabled = this.user.twoFactorAuthenticationEnabled;
      // O perfil não muda por aqui: continua na store, mas não vai no PATCH `auth/user/me`.
      payload.profiles = this.user.profiles;
      const user = cloneDeep(payload);
      this.user = cloneDeep(payload);
      delete user.profiles;
      delete user.authorities;
      delete user.username;
      delete user.id;

      await ServiceAuth.user.update(user);
      // Perfil/permissões podem ter mudado no backend: recarrega o usuário (`auth/user/me`) na store.
      await this.getUser();
    },
    changeModal(payload) {
      this.showModal = payload;
    },
    async signIn(credentials) {
      this.removeToken();
      this.loading = true;
      this.setCredentials(credentials);
      try {
        const { data } = await ServiceAuth.user.login(credentials);
        if (data) {
          return this.setTokens(data);
        }
      } catch (e) {
        console.error(e);
        this.errorSubmit(e);
      }
    },

    async checkUserForChange(credentials) {
      try {
        const response = await ServiceAuth.user.login(credentials, { confirmingPassword: true });
        if (response.status === 200) {
          this.validationUserErrors = '';

          this.userValidated = true;
        }
      } catch (e) {
        if (e.status === 302) {
          this.validationUserErrors = '';
          this.requireToken = true;
        }
        if (e.status === 400) {
          this.validationUserErrors = e.response.data.errors[1];
        }
      }
    },
    async loginTwofa(payload) {
      this.removeToken();
      this.loading = true;

      try {
        const { data } = await ServiceAuth.user.loginTwofa(payload);

        if (data) {
          return this.setTokens(data);
        }
      } catch (e) {
        this.errorSubmit(e);
      }
    },
    removeToken() {
      localStorage.removeItem('access_Token');
      localStorage.removeItem('refresh_Token');
      localStorage.removeItem('access_token_expiration_date');
    },
    cleanError(payload) {
      this.error = payload;
    },
    errorSubmit(e) {
      if (e.message.includes(503) || e.message.includes(500)) {
        this.error = 'Serviço indisponível, tente em um momento';
        this.setMessage('');
      } else if (e.message.includes(401) || e.message.includes(400)) {
        this.error = e.response.data.errors?.[1] || 'Usuário ou senha inválidos';
        this.setMessage('');
      } else if (e.message.includes(404)) {
        this.error = e.response.data.errors?.[1] || 'Erro não mapeado';
        this.setMessage('');
      } else if (e.message.includes(302)) {
        this.error = e.response.data.errors?.[1] || 'Redirecionando...';
        this.setMessage('');
      } else {
        this.error = e.message;
        this.setMessage('');
      }

      this.token = null;
      this.refreshToken = null;
      this.tokenExpirationDate = null;
      this.user = null;
    },
    async setTokens(payload) {
      this.error = null;
      this.token = payload.access_token;
      this.refreshToken = payload.refresh_token;
      this.tokenExpirationDate = Math.floor(Date.now() / 1000) + payload.expires_in;

      localStorage.setItem('access_Token', this.token);
      localStorage.setItem('refresh_Token', this.refreshToken);
      localStorage.setItem('access_token_expiration_date', this.tokenExpirationDate);

      return this.getUser();
    },
    async setCredentials(payload) {
      this.credentials = payload;
    },
    setTwoFactorAuthentication(payload) {
      this.user.twoFactorAuthenticationEnabled = payload;
    },
    async fetchUserEstablishments() {
      if (!this.user?.id) return;

      try {
        this.loadingUserEstablishments = true;
        const { data } = await ServiceAdmin.userEstablishment.get(this.user.id);
        let userEstablishments = data.map((item) => item.establishment);
        userEstablishments = sortBy(userEstablishments, ['socialName', 'stateCode', 'city']);

        this.userEstablishments = userEstablishments;
      } catch (e) {
        console.error('Error fetching user establishments:', e);
      } finally {
        this.loadingUserEstablishments = false;
      }
    },
    async wasLast3Passwords1(data) {
      try {
        const response = await ServiceAuth.passwordReset.wasLast3Passwords(data);
        this.validPassword = response.data;
        return response.data;
      } catch (e) {
        console.error(e);
      }
    },
    hasPermission(authorityNeeded) {
      return this.authorities.some((i) => authorityNeeded.includes(i));
    },
    hasViewPermission(feature) {
      if (this.isAdmin) return true;

      const permissionMap = {
        solicitacao: [
          USER_ROLES.ROLE_MENU_GDT_SOLICITACAO_VISUALIZACAO,
          USER_ROLES.ROLE_MENU_GDT_SOLICITACAO_GERENCIAMENTO,
        ],
        envioDocumento: [USER_ROLES.ROLE_MENU_GDT_ENVIO_VISUALIZACAO, USER_ROLES.ROLE_MENU_GDT_ENVIO_GERENCIAMENTO],
      };

      const requiredRoles = permissionMap[feature];
      if (!requiredRoles) return false;

      return this.authorities.some((authority) => requiredRoles.includes(authority));
    },
    hasManagePermission(feature) {
      if (this.isAdmin) return true;

      const permissionMap = {
        solicitacao: ['ROLE_MENU_GDT_SOLICITACAO_GERENCIAMENTO'],
        envioDocumento: ['ROLE_MENU_GDT_ENVIO_GERENCIAMENTO'],
      };

      const requiredRoles = permissionMap[feature];
      if (!requiredRoles) return false;

      return this.authorities.some((authority) => requiredRoles.includes(authority));
    },
    canAccessScreen(screenRoles) {
      if (this.isAdmin) return true;

      if (!screenRoles || screenRoles.length === 0) return true;

      return this.authorities.some((authority) => screenRoles.includes(authority));
    },
  },
});

function parseListWithoutGroups(establishments) {
  return establishments.map((item) => {
    return {
      id: item.details.cnpj,
      title: item.socialName,
      value: item.details.cnpj,
      isGroup: false,
    };
  });
}

function getEstablishmentsGroupedBySocialName(establishments) {
  return groupBy(establishments, 'socialName');
}

function parseListGroupItems(establishmentsGroupedBySocialName) {
  return Object.entries(establishmentsGroupedBySocialName).map(([socialName, establishments]) =>
    parseListGroupItem(socialName, establishments),
  );
}

function parseListGroupItem(groupItemName, items) {
  const groupId = uniqueId('list_group_item_');

  return {
    id: groupId,
    isGroup: true,
    title: groupItemName,
    items: items.map((establishment) => parseListItem(groupId, establishment)),
  };
}
function parseListItem(groupId, establishment) {
  return {
    id: establishment.id,
    groupId,
    title: establishment.details.socialName,
    subtitle: UtilsMask.cnpj(establishment.details.cnpj),
    value: establishment.details.cnpj,
    details: establishment.details,
  };
}

function separateAndFlattenGroupItems(groupItems) {
  const flattenedItems = [];

  groupItems.forEach(({ items, ...rest }) => {
    flattenedItems.push(rest);
    flattenedItems.push(...items);
  });

  return flattenedItems;
}
